import type { KpiCardSpec } from '~containers/analytics/kpis/card-spec';
import type { KpiCardData } from '~containers/analytics/kpis/types';

const AVERAGES_NOTE =
    'The averages spread the selected period evenly over its own days, and read N/A (Date N/A on hover) on a window shorter than a week.';

/** Unlike `AVERAGES_NOTE` above: each of the three figures is its own real
 * read over its own window, never one divided down from another. */
const RATE_WINDOWS_NOTE =
    "Weekly avg. and Daily avg. are real reads over the last 7 days and the last day of the period, not averages, and both are cut to the campaign's own dates; Weekly avg. reads N/A (Date N/A on hover) when that leaves fewer than 7 days.";

/**
 * Shared by the Enrollment tab's Campaign details row and the Members and
 * visits tab's Member details row: one object, so both tabs show the same
 * card by identity rather than two descriptions a future edit could drift
 * apart.
 */
export const ACTIVE_MEMBERS_CARD: KpiCardSpec = {
    title: 'Active members',
    source: 'campaign-members',
    metric: 'activeMembers',
    form: 'trio',
    size: 'sm',
    trend: false,
    description: `Fully enrolled members with at least one transaction, not counting the automatic enrollment visit. ${AVERAGES_NOTE}`,
};

/**
 * Campaign details: the headline row of the General tab. Every card is a `trio`,
 * reading the campaign-to-date window the page resolves off the club alongside
 * the selected period.
 */
export const CAMPAIGN_DETAILS_CARDS: KpiCardSpec[] = [
    {
        title: 'Enrollment',
        source: 'campaign-members',
        metric: 'enrolledMembers',
        form: 'trio',
        size: 'sm',
        trend: false,
        description: `Members who enrolled in the Road to Rewards campaign, counted on the day they enrolled. ${AVERAGES_NOTE}`,
    },
    ACTIVE_MEMBERS_CARD,
    {
        title: 'Store visits',
        source: 'campaign-purchases',
        metric: 'storeVisits',
        form: 'trio',
        size: 'sm',
        trend: false,
        description: `Store visits recorded at the campaign LIFT screen. ${AVERAGES_NOTE}`,
    },
    {
        title: 'Issued coupons',
        source: 'campaign-coupons-issued',
        metric: 'couponsIssued',
        form: 'trio',
        size: 'sm',
        trend: false,
        description: `Coupons granted to Road to Rewards members during the campaign, counted on the day they were issued. ${AVERAGES_NOTE}`,
    },
    {
        title: 'Campaign spend',
        source: 'campaign-purchases',
        metric: 'spend',
        form: 'trio',
        size: 'sm',
        trend: false,
        description: `Spend from member baskets at the campaign LIFT screen. ${AVERAGES_NOTE}`,
    },
];

export const ENROLLMENT_FUNNEL_CARDS: KpiCardSpec[] = [
    {
        title: 'Campaign enrollment funnel',
        source: 'campaign-enrollment-funnel',
        form: 'bars',
        size: 'lg',
        palette: 'campaign',
        barsStyle: 'filled',
        description:
            'Fully enrolled members against those who went on to make a qualifying purchase.',
    },
];

/** Unlike the funnel above, nothing pins this card's window. */
export const ENROLLMENT_BY_BU_CARDS: KpiCardSpec[] = [
    {
        title: 'Campaign enrollment count by BU',
        source: 'campaign-members',
        metric: 'enrolledMembers',
        form: 'columns',
        breakdownBy: 'businessUnit',
        size: 'lg',
        palette: 'campaign',
        shareAxis: true,
        description:
            'Enrolled members split by business unit, following the page Period and Region filters.',
    },
];

export const ENROLLMENT_OVER_TIME_CARDS: KpiCardSpec[] = [
    {
        title: 'Campaign enrollment over time',
        source: 'campaign-members',
        form: 'multiline',
        size: 'lg',
        palette: 'campaign',
        chartStyle: 'area',
        description:
            'Fully enrolled members and those who went on to make a qualifying purchase. Each point counts who reached that state on the day itself, not a running total.',
    },
];

/**
 * Shared by the Members and visits tab's Member details and Members visits
 * rows: one object, so the two Repeat store visit rate cards never disagree.
 */
export const REPEAT_STORE_VISIT_RATE_CARD: KpiCardSpec = {
    title: 'Repeat store visit rate',
    source: 'campaign-visits',
    metric: 'repeatVisitRate',
    form: 'trio',
    size: 'sm',
    trend: false,
    description:
        'Share of enrolled members with more than one true visit, not counting the automatic enrollment visit.',
};

/**
 * Member details: two rows of three, the headline row of the Members and
 * visits tab. `ACTIVE_MEMBERS_CARD` and `REPEAT_STORE_VISIT_RATE_CARD` are
 * the same objects their other tab or row uses, so totals never disagree.
 */
export const MEMBER_DETAILS_CARDS: KpiCardSpec[] = [
    ACTIVE_MEMBERS_CARD,
    {
        title: 'Average store visit frequency',
        source: 'campaign-visits',
        metric: 'avgVisitsPerMember',
        form: 'rate-trio',
        size: 'sm',
        trend: false,
        description: `True visits per enrolled member, not counting the automatic enrollment visit. ${RATE_WINDOWS_NOTE}`,
    },
    REPEAT_STORE_VISIT_RATE_CARD,
    {
        title: 'Average amount spent/transaction',
        source: 'campaign-purchases',
        metric: 'avgSpendPerTransaction',
        form: 'rate-trio',
        size: 'sm',
        trend: false,
        description: `Spend from member baskets at the campaign LIFT screen, divided by qualifying purchases. ${RATE_WINDOWS_NOTE}`,
    },
    {
        title: 'Average spent/member',
        source: 'campaign-purchases',
        metric: 'avgSpendPerMember',
        form: 'trio',
        size: 'sm',
        trend: false,
        description: `Spend from member baskets at the campaign LIFT screen, divided by every enrolled member. ${AVERAGES_NOTE}`,
    },
    {
        title: 'Average units/transaction',
        source: 'campaign-purchases',
        metric: 'avgUnitsPerTransaction',
        form: 'rate-trio',
        size: 'sm',
        trend: false,
        description: `Units from member baskets at the campaign LIFT screen, divided by qualifying purchases. ${RATE_WINDOWS_NOTE}`,
    },
];

/**
 * Coupons overview: the seven specs of that grid, in row order. The page
 * inserts the Total coupons / Remaining coupons stock cards (not specs here)
 * between the sixth and seventh, ahead of Expired coupons.
 */
export const COUPONS_OVERVIEW_CARDS: KpiCardSpec[] = [
    {
        title: 'Issued milestone coupons',
        source: 'campaign-coupons-member',
        metric: 'couponsIssued',
        form: 'trio',
        size: 'sm',
        trend: false,
        description: `Milestone ladder coupons granted to members, counted on the day they were issued. ${AVERAGES_NOTE}`,
    },
    {
        title: 'Redeemed coupons',
        source: 'campaign-coupons-member',
        metric: 'couponsRedeemed',
        form: 'trio',
        size: 'sm',
        trend: false,
        description: `Milestone ladder coupons redeemed in store, counted on the day of redemption. ${AVERAGES_NOTE}`,
    },
    {
        title: 'Coupon redemption rate',
        source: 'campaign-coupons-member',
        metric: 'couponRedemptionRate',
        form: 'rate-trio',
        size: 'sm',
        trend: false,
        description: `Share of milestone ladder coupons issued in each window that were later redeemed. The numerator counts differently from the Redeemed coupons card, which counts redemptions inside the window regardless of when the coupon was issued. ${RATE_WINDOWS_NOTE}`,
    },
    {
        title: 'Issued members',
        source: 'campaign-coupons-member',
        metric: 'issuedMembers',
        form: 'trio',
        size: 'sm',
        trend: false,
        description: `Distinct members granted at least one milestone ladder coupon, counted by phone number rather than by account. ${AVERAGES_NOTE}`,
    },
    {
        title: 'Redeemed members',
        source: 'campaign-coupons-member',
        metric: 'redeemedMembers',
        form: 'trio',
        size: 'sm',
        trend: false,
        description: `Distinct members who redeemed at least one milestone ladder coupon, counted by phone number rather than by account. ${AVERAGES_NOTE}`,
    },
    {
        title: 'Member redemption rate',
        source: 'campaign-coupons-member',
        metric: 'memberRedemptionRate',
        form: 'rate-trio',
        size: 'sm',
        trend: false,
        description: `Share of members granted a milestone ladder coupon in each window who redeemed at least one since. The numerator counts differently from the Redeemed members card, which counts redemptions inside the window regardless of when the member was granted a coupon. ${RATE_WINDOWS_NOTE}`,
    },
    {
        title: 'Expired coupons',
        source: 'campaign-coupons-member',
        metric: 'expiredCoupons',
        form: 'trio',
        size: 'sm',
        trend: false,
        toDateOnly: true,
        description:
            'Milestone ladder coupons that expired without being redeemed.',
    },
];

/**
 * Members visits: two half-width cards on `campaign-visits`.
 * `REPEAT_STORE_VISIT_RATE_CARD` is the same object Member details uses.
 */
export const MEMBERS_VISITS_CARDS: KpiCardSpec[] = [
    REPEAT_STORE_VISIT_RATE_CARD,
    {
        title: 'Returning members',
        source: 'campaign-visits',
        metric: 'returningMembers',
        form: 'trio',
        size: 'sm',
        trend: false,
        description: `Enrolled members with more than one true visit, not counting the automatic enrollment visit: roughly milestone 3 and up in the chart below, since the chart also counts free contest entries. ${AVERAGES_NOTE}`,
    },
];

/**
 * The population-scope marker every card below (and the section heading
 * beside them) carries.
 *
 * Carried on every one of the seven cards, not only the section header: a
 * reader scrolling into the middle of this section must not have to scroll
 * back up to know which population they are looking at. One shared constant
 * so the card copy and the section subtitle (`index.tsx`) can never drift
 * apart into two different claims.
 */
export const CAMPAIGN_VISITS_LEDGER_SCOPE_MARKER = 'Any phone, enrolled or not';

/** Every LIFT row Six12 sent in the window, whatever its status, on any phone. */
export const CAMPAIGN_VISITS_LEDGER_CARDS: KpiCardSpec[] = [
    {
        title: 'Visits received',
        subtitle: CAMPAIGN_VISITS_LEDGER_SCOPE_MARKER,
        source: 'campaign-visits-ledger',
        metric: 'visitsReceived',
        form: 'trio',
        size: 'sm',
        trend: false,
        description: `Every LIFT visit Six12 sent in the window, whatever its status. ${AVERAGES_NOTE}`,
    },
    {
        title: 'Counted as visits',
        subtitle: CAMPAIGN_VISITS_LEDGER_SCOPE_MARKER,
        source: 'campaign-visits-ledger',
        metric: 'visitsCounted',
        form: 'trio',
        size: 'sm',
        trend: false,
        description: `Visits received that cleared every count rule below. ${AVERAGES_NOTE}`,
    },
    {
        title: 'Rejected: under $5',
        subtitle: CAMPAIGN_VISITS_LEDGER_SCOPE_MARKER,
        source: 'campaign-visits-ledger',
        metric: 'rejectedUnderMinSpend',
        form: 'trio',
        size: 'sm',
        trend: false,
        description: `Rejected because the purchase did not meet the $5 minimum for a visit to count. ${AVERAGES_NOTE}`,
    },
    {
        title: 'Rejected: within 3h',
        subtitle: CAMPAIGN_VISITS_LEDGER_SCOPE_MARKER,
        source: 'campaign-visits-ledger',
        metric: 'rejectedGapTooShort',
        form: 'trio',
        size: 'sm',
        trend: false,
        description: `Rejected for landing within 3 hours of another counted visit on that same phone. ${AVERAGES_NOTE}`,
    },
    {
        title: 'Rejected: daily cap',
        subtitle: CAMPAIGN_VISITS_LEDGER_SCOPE_MARKER,
        source: 'campaign-visits-ledger',
        metric: 'rejectedDailyCap',
        form: 'trio',
        size: 'sm',
        trend: false,
        description: `Rejected because that phone had already reached its daily visit cap. ${AVERAGES_NOTE}`,
    },
    {
        title: 'Revenue received',
        subtitle: CAMPAIGN_VISITS_LEDGER_SCOPE_MARKER,
        source: 'campaign-visits-ledger',
        metric: 'revenueReceived',
        form: 'trio',
        size: 'sm',
        trend: false,
        description: `Total price of every visit received, counted or not. ${AVERAGES_NOTE}`,
    },
    {
        title: 'Revenue counted',
        subtitle: CAMPAIGN_VISITS_LEDGER_SCOPE_MARKER,
        source: 'campaign-visits-ledger',
        metric: 'revenueCounted',
        form: 'trio',
        size: 'sm',
        trend: false,
        description: `Total price of counted visits only. ${AVERAGES_NOTE}`,
    },
];

export const MEMBERS_VISITS_PER_MILESTONE_CARDS: KpiCardSpec[] = [
    {
        title: 'Members visits per milestone',
        source: 'campaign-milestones',
        metric: 'membersOnLadder',
        form: 'columns',
        breakdownBy: 'visitMilestone',
        size: 'lg',
        palette: 'milestone',
        subLabelUnit: 'days',
        description:
            'Members at each visit count to date, exclusive: Visit 1 is members with only their enrollment bonus, and 15 holds everyone at 15 or more. The six rungs drawn in dark blue grant a reward.',
    },
];

/** Ticket wording: shown greyed out "with empty numbers", not a fabricated
 * split. */
const EMPTY_DONUT_SAMPLE: KpiCardData = {
    form: 'pie',
    slices: [
        { label: 'Reachable', value: 0 },
        { label: 'Not opted in', value: 0 },
    ],
};

/**
 * Members Contactability: four donuts on one row, mockup order (Email,
 * SMS/MMS, Push notifications, Mobile), each headlined by its first slice.
 * Email sits on the placeholder source until product decides what email
 * reachability means; the other three read `campaign-contactability`.
 */
export const MEMBERS_CONTACTABILITY_CARDS: KpiCardSpec[] = [
    {
        title: 'Email',
        source: 'placeholder',
        form: 'pie',
        size: 'sm',
        headlineFromFirstSlice: true,
        legendBelow: true,
        palette: 'campaign',
        sample: EMPTY_DONUT_SAMPLE,
        description:
            'Cut from this release by product decision: which signal should define email reachability is still unresolved.',
    },
    {
        title: 'SMS/MMS',
        source: 'campaign-contactability',
        metric: 'smsReachable',
        form: 'pie',
        size: 'sm',
        headlineFromFirstSlice: true,
        legendBelow: true,
        palette: 'campaign',
        description:
            'Members opted in to SMS as of our last sync with Six12. A change made with Six12 directly shows after the next sync. Members not synced yet count as not opted in.',
    },
    {
        title: 'Push notifications',
        source: 'campaign-contactability',
        metric: 'pushReachable',
        form: 'pie',
        size: 'sm',
        headlineFromFirstSlice: true,
        legendBelow: true,
        palette: 'campaign',
        description:
            'Members with a push token and notifications not explicitly disabled, the same predicate the send path filters on. A member with no recorded preference counts as reachable.',
    },
    {
        title: 'Mobile',
        source: 'campaign-contactability',
        metric: 'phoneVerified',
        form: 'pie',
        size: 'sm',
        headlineFromFirstSlice: true,
        legendBelow: true,
        palette: 'campaign',
        description: 'Members with a phone number confirmed at enrollment.',
    },
];
